import { eagerLazy } from "@/lib/eagerLazy";

/**
 * Eager-lazy registry for app-level chrome that ships baked into the
 * prerendered HTML of every marketing route.
 *
 * The prerender script captures the fully-mounted React tree, which means
 * the shadcn `<Toaster>` (Radix toast region) and Sonner `<Toaster>` both
 * render their viewport DOM nodes inside `#root`. If those modules are
 * still `React.lazy()` on the client, their top-level `<Suspense
 * fallback={null}>` in `App.tsx` renders `null` on the first hydration
 * pass — where the prerendered HTML has real `<div role="region">` /
 * `<section aria-label="Notifications alt+T">` nodes — and React logs
 * #418 then escalates to #423, discarding hydration entirely.
 *
 * Preloading these chunks before `hydrateRoot` lets them render
 * synchronously on first mount so hydration adopts the prerendered DOM
 * cleanly. Non-prerendered consumers still get standard `React.lazy`
 * behaviour (suspend on first use) if `preload()` is never called.
 */

export const ToasterLazy = eagerLazy(() =>
  import("@/components/ui/toaster").then((m) => ({ default: m.Toaster }))
);

export const SonnerLazy = eagerLazy(() =>
  import("@/components/ui/sonner").then((m) => ({ default: m.Toaster }))
);

export const BrandingStyleInjectorLazy = eagerLazy(() =>
  import("@/components/layout/BrandingStyleInjector").then((m) => ({
    default: m.BrandingStyleInjector,
  }))
);

const ALL_APP_PRELOADS = [
];

// Phase A — Toaster + Sonner removed from awaited preloads.
// Their snapshot DOM is stripped by `stripToasterDom` in the prerender
// script, so the static HTML at App.tsx's `<Suspense fallback={null}>`
// position is already empty when createRoot mounts. First render produces
// matching null content (no flash, no #418/#423). The chunks are fetched
// lazily on demand the first time a form calls `toast(...)`. They are
// warmed up in the background via `warmAppChunksIdle` so even the very
// first interaction feels instant.
// BrandingStyleInjectorLazy is intentionally NOT preloaded — it moved to
// <DashboardProviders> and no longer renders inside the prerendered tree.

/**
 * Fire every app-level prerendered chunk import in parallel. Resolves
 * once all chunks are downloaded so hydration can render them
 * synchronously and match the static HTML.
 */
export async function preloadAppChunks(): Promise<void> {
  await Promise.all(ALL_APP_PRELOADS.map((p) => p()));
}

/**
 * Phase 3 — Toaster/Sonner chunks are now mounted lazily by
 * `LazyToastHost` on the first user interaction. Idle-warming them
 * during boot would re-introduce them to the initial homepage request
 * waterfall (which PSI flagged as a deep critical-path chain), so this
 * helper is intentionally a no-op. Kept exported for backwards
 * compatibility with `main.tsx`.
 */
export function warmAppChunksIdle(): void {
  // intentionally empty — see comment above.
}